import React, {ChangeEvent, Component, ReactNode} from 'react'
import {NoSelectedValue} from '../../../componentHelper/selectHelper'
import {ReservationOption, ReserveFacilityModel} from '../ReserveFacilityModel'
import {VALID_OR_NOT} from '../../../CodeMaster'
import DisplayItemSelectComponent from '../../../styledComponents/DisplayItemSelectComponent'
import {
    DisplayItemDivider,
    DisplayItemFacilityOptionSubtitle,
    DisplayItemFacilityOptionTitle,
} from '../../../styledComponents/DisplayItem'
import {BlockSpacer, GridContainer, GridItem, HideInPC, HideInSP} from '../../../styledComponents/ResponsiveConvenience'
import {compareFacilityOption} from '../helpers/compareHelper'

export interface FacilityOption {
    optionId: number
    optionName: string
    outline: string
    minimumQuantity: number
    maximumQuantity: number
    unit: string
    required: VALID_OR_NOT
    sortOrder: number
}

interface Props {
    reservationModel: ReserveFacilityModel
    facilityOptions: FacilityOption[]
    reservationOptions: ReservationOption[]
}

interface State {
    reservationOptions: ReservationOption[]
}

class RRFFReserveOptionInput extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            reservationOptions: props.reservationOptions,
        }
        this.handleChangeOptionQuantity = this.handleChangeOptionQuantity.bind(this)
    }

    componentDidMount(): void {
        const {facilityOptions, reservationOptions} = this.props

        const initialReservationOptions = facilityOptions.map(facilityOption => {
                let quantity = 0
                const reservationOption = reservationOptions
                    .find(rop => rop.optionId === facilityOption.optionId)
                if (reservationOption !== undefined) {
                    quantity = reservationOption.quantity

                    if (0 < quantity &&
                        (quantity < facilityOption.minimumQuantity ||
                            facilityOption.maximumQuantity < quantity)
                    ) {
                        quantity = 0
                    }
                }

                if (facilityOption.required === VALID_OR_NOT.VALID && quantity === 0) {
                    quantity = facilityOption.minimumQuantity
                }

                return {
                    optionId: facilityOption.optionId,
                    quantity: quantity,
                    sortOrder: facilityOption.sortOrder,
                }
            },
        ).filter(option => option.quantity > 0,
        ).sort((a, b) => a.sortOrder - b.sortOrder)

        this.setState({
            reservationOptions: initialReservationOptions,
        }, () => {
            this.updateModel()
        })
    }

    updateModel(): void {
        const reservationOptions = this.state.reservationOptions
            .filter(option => option.quantity > 0)
            .map(option => {
                    return {
                        optionId: option.optionId,
                        quantity: option.quantity,
                        sortOrder: option.sortOrder,
                    }
                },
            )
            .sort((a, b) => a.sortOrder - b.sortOrder)
        this.props.reservationModel.withReservationOptions(reservationOptions)
    }

    handleChangeOptionQuantity(event: ChangeEvent<HTMLSelectElement>): void {
        const changedOptionId = Number(event.target.id)
        const changedOptionValue = event.target.value === NoSelectedValue ? 0 : Number(event.target.value)
        const facilityOption = this.props.facilityOptions.find(option => option.optionId === changedOptionId)

        if (!facilityOption) {
            throw Error('(!facilityOption)')
        }

        this.setState((prevState) => {
            const {reservationOptions} = prevState
            const changedOptionIndex =
                reservationOptions.findIndex(option => (option.optionId === changedOptionId))

            const targetReservationOption = {
                optionId: changedOptionId,
                quantity: changedOptionValue,
                sortOrder: facilityOption.sortOrder,
            }

            if (changedOptionIndex === -1) {
                const appended: { reservationOptions: ReservationOption[] } = {
                    reservationOptions: [
                        targetReservationOption,
                        ...reservationOptions,
                    ].sort((a, b) => a.sortOrder - b.sortOrder),
                }
                return appended
            } else {
                const replaced: { reservationOptions: ReservationOption[] } = {
                    reservationOptions: [
                        ...reservationOptions.slice(0, changedOptionIndex),
                        targetReservationOption,
                        ...reservationOptions.slice(changedOptionIndex + 1),
                    ].sort((a, b) => a.sortOrder - b.sortOrder),
                }
                return replaced
            }
        }, () => {
            this.updateModel()
        })
    }

    render(): ReactNode {
        const {facilityOptions} = this.props
        const {reservationOptions} = this.state
        if (facilityOptions.length === 0) {
            return null
        }

        return (<>
            <BlockSpacer height={'30px'}/>
            <DisplayItemDivider/>
            {
                this.props.facilityOptions
                    .sort((a, b) => compareFacilityOption(a, b))
                    .map((facilityOption) => {
                        const targetOption: ReservationOption | undefined =
                            reservationOptions.find(reservationOption =>
                                reservationOption.optionId === facilityOption.optionId)
                        const defaultValue =
                            (targetOption) ? `${targetOption.quantity}` : NoSelectedValue
                        const required = facilityOption.required === VALID_OR_NOT.VALID
                        return (<div key={facilityOption.optionId}>
                                <BlockSpacer height={'30px'}/>
                                <GridContainer>

                                    <GridItem over={3}>
                                        <DisplayItemFacilityOptionTitle required={required}>
                                            {facilityOption.optionName}
                                        </DisplayItemFacilityOptionTitle>
                                        <HideInPC>
                                            <BlockSpacer height={'10px'}/>
                                            <DisplayItemFacilityOptionSubtitle>
                                                {facilityOption.outline}
                                            </DisplayItemFacilityOptionSubtitle>
                                        </HideInPC>
                                    </GridItem>
                                    <GridItem over={9}>
                                        <BlockSpacer height={'10px'}/>
                                        <DisplayItemSelectComponent
                                            value={defaultValue}
                                            onChange={this.handleChangeOptionQuantity}
                                            id={`${facilityOption.optionId}`}
                                        >
                                            {
                                                <SelectOptionsForQuantity
                                                    min={facilityOption.minimumQuantity}
                                                    max={facilityOption.maximumQuantity}
                                                    required={facilityOption.required === VALID_OR_NOT.VALID}
                                                    unit={facilityOption.unit}
                                                />
                                            }
                                        </DisplayItemSelectComponent>
                                    </GridItem>

                                    <GridItem over={3}/>
                                    <GridItem over={9}>
                                        <HideInSP>
                                            <BlockSpacer height={'10px'}/>
                                            <DisplayItemFacilityOptionSubtitle>
                                                {facilityOption.outline}
                                            </DisplayItemFacilityOptionSubtitle>
                                        </HideInSP>
                                    </GridItem>
                                </GridContainer>
                            </div>
                        )
                    })
            }
        </>)
    }
}

interface SelectOptionsForQuantityProps {
    min: number
    max: number
    required: boolean
    unit: string
}

const SelectOptionsForQuantity: React.FC<SelectOptionsForQuantityProps> = props => {
    const selectableItems =
        new Array<number>((props.max - props.min + 1))
            .fill(0)
            .map((_, index) => (props.min + index))
    const selectableOptionItems = selectableItems.map(item => ({value: item, label: `${item}${props.unit}`}))
    const optionItems = (props.required)
        ? selectableOptionItems
        : [{value: NoSelectedValue, label: '選択なし'}, ...selectableOptionItems]

    return <>{
        optionItems.map(item => {
            return (
                <option key={item.value} value={item.value}>
                    {item.label}
                </option>
            )
        })
    }</>
}

export default RRFFReserveOptionInput
